/**
 * field.recent_touches — most recent member touches across the workspace.
 * Pulls members-index, sorts by last_touch_date desc, shows top N with
 * member name + days since last touch.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";

function daysAgo(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86_400_000);
}

export function FieldRecentTouches({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 8;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "field.recent_touches", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/members-index`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Recent touches could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" className="space-y-1.5"><span className="sr-only">Loading…</span><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-3/4" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-1/2" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-2/3" /></div>;
  const members: any[] = data?.members ?? [];
  const recent = members
    .filter((m) => m.last_touch_date)
    .sort((a, b) => new Date(b.last_touch_date).getTime() - new Date(a.last_touch_date).getTime())
    .slice(0, limit);

  if (recent.length === 0) {
    return <div className="text-stone-600 text-xs">No touches recorded yet. Log a meeting or call with a member (or ask your AI client) and it will show up here.</div>;
  }

  return (
    <ul className="space-y-1 text-xs">
      {recent.map((m) => {
        const d = daysAgo(m.last_touch_date);
        return (
          <li key={m.bioguide_id}>
            <Link
              to={`/members/${encodeURIComponent(m.bioguide_id)}`}
              className="flex items-center gap-2 hover:bg-stone-50 px-1 py-0.5 rounded -mx-1"
            >
              <span className="flex-1 truncate text-stone-800">{m.display_name}</span>
              <span className="font-mono text-stone-500 text-[10px]">{m.state}</span>
              <span className="font-mono text-stone-500 text-[10px] tabular-nums">{d === 0 ? "today" : `${d}d ago`}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
